import React, { useState } from 'react';

interface PieChartDataPoint {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieChartDataPoint[];
  size?: number;
  innerRadius?: number; // For donut chart (0 = pie, 0.5 = donut)
  showLegend?: boolean;
  showPercentages?: boolean;
  className?: string;
}

/**
 * Pie/Donut Chart Component
 * Visualizes proportional data in a circular format
 */
const PieChart: React.FC<PieChartProps> = ({
  data,
  size = 250,
  innerRadius = 0,
  showLegend = true,
  showPercentages = true,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ height: size }}>
        <div className="text-gray-400 text-sm">No data available</div>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const center = size / 2;
  const radius = (size * 0.8) / 2;
  const innerRadiusValue = radius * innerRadius;

  // Calculate pie slice paths
  let currentAngle = -90; // Start at top

  const slices = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const angle = (item.value / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;

    currentAngle += angle;

    // Convert to radians
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    // Calculate arc points
    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    const largeArcFlag = angle > 180 ? 1 : 0;

    let path: string;

    if (innerRadius > 0) {
      // Donut chart
      const ix1 = center + innerRadiusValue * Math.cos(startRad);
      const iy1 = center + innerRadiusValue * Math.sin(startRad);
      const ix2 = center + innerRadiusValue * Math.cos(endRad);
      const iy2 = center + innerRadiusValue * Math.sin(endRad);

      path = `
        M ${x1} ${y1}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
        L ${ix2} ${iy2}
        A ${innerRadiusValue} ${innerRadiusValue} 0 ${largeArcFlag} 0 ${ix1} ${iy1}
        Z
      `;
    } else {
      // Pie chart
      path = `
        M ${center} ${center}
        L ${x1} ${y1}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
        Z
      `;
    }

    // Calculate label position (middle of slice)
    const midAngle = (startAngle + endAngle) / 2;
    const midRad = (midAngle * Math.PI) / 180;
    const labelRadius = innerRadius > 0 ? (radius + innerRadiusValue) / 2 : radius * 0.7;
    const labelX = center + labelRadius * Math.cos(midRad);
    const labelY = center + labelRadius * Math.sin(midRad);

    return {
      path,
      item,
      percentage,
      labelX,
      labelY,
      index,
    };
  });

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* Chart */}
      <svg width={size} height={size} className="overflow-visible">
        {slices.map((slice) => (
          <g key={slice.index}>
            <path
              d={slice.path}
              fill={slice.item.color}
              stroke="white"
              strokeWidth="2"
              opacity={hoveredIndex === null || hoveredIndex === slice.index ? 1 : 0.6}
              className="transition-all duration-200 cursor-pointer"
              onMouseEnter={() => setHoveredIndex(slice.index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                transform: hoveredIndex === slice.index ? 'scale(1.05)' : 'scale(1)',
                transformOrigin: `${center}px ${center}px`,
              }}
            />
            {showPercentages && slice.percentage > 5 && (
              <text
                x={slice.labelX}
                y={slice.labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-sm font-semibold fill-white pointer-events-none"
                style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                {slice.percentage.toFixed(0)}%
              </text>
            )}
          </g>
        ))}

        {/* Center text for donut charts */}
        {innerRadius > 0 && (
          <g>
            <text
              x={center}
              y={center - 10}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-2xl font-bold fill-gray-900"
            >
              {total.toLocaleString()}
            </text>
            <text
              x={center}
              y={center + 15}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-sm fill-gray-600"
            >
              Total
            </text>
          </g>
        )}
      </svg>

      {/* Legend */}
      {showLegend && (
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 w-full max-w-md">
          {data.map((item, index) => {
            const percentage = ((item.value / total) * 100).toFixed(1);
            return (
              <div
                key={index}
                className="flex items-center gap-2 cursor-pointer transition-opacity"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  opacity: hoveredIndex === null || hoveredIndex === index ? 1 : 0.5,
                }}
              >
                <div
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{item.label}</div>
                  <div className="text-xs text-gray-600">
                    {item.value.toLocaleString()} ({percentage}%)
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PieChart;
